import { useCallback, useEffect } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Group,
  Paper,
  Button,
  Tabs,
  Badge,
  Loader,
  Center,
  Modal,
  Divider,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconEdit, IconX } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  getPrefixById,
  deactivatePrefix,
  listPrefixes,
} from "../../../services/prefixService";
import { getContrastColor } from "../../../utils/helpers";
import PrefixList from "../../../components/admin/prefixes/PrefixList";
import PrefixFormModal from "../../../components/admin/prefixes/PrefixFormModal";
import PrefixMappingsPanel from "../../../components/admin/prefixes/PrefixMappingsPanel";
import PrefixAllowedBillingScenariosPanel from "../../../components/admin/prefixes/PrefixAllowedBillingScenariosPanel";
import PrefixInsuranceEligibilityPanel from "../../../components/admin/prefixes/PrefixInsuranceEligibilityPanel";
import { PrefixReportIdentityPanel } from "../../../components/admin/prefixes/PrefixReportIdentityPanel";

const VALID_TABS = [
  "mappings",
  "case-types",
  "eligibility",
  "report-identity",
] as const;

export default function PrefixConfigurationAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const [
    deactivateModalOpened,
    { open: openDeactivateModal, close: closeDeactivateModal },
  ] = useDisclosure(false);

  const queryClient = useQueryClient();

  const { data: prefixListData } = useQuery({
    queryKey: ["prefixes", { includeInactive: true }],
    queryFn: () => listPrefixes({ includeInactive: true, limit: 100 }),
  });

  const prefixParam = searchParams.get("prefix");
  const effectiveSelectedId =
    prefixParam ?? prefixListData?.data?.[0]?.id ?? null;

  const tabParam = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(tabParam as (typeof VALID_TABS)[number])
    ? (tabParam as string)
    : "mappings";

  const handleSelectPrefix = useCallback(
    (prefixId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("prefix", prefixId);
        return next;
      });
    },
    [setSearchParams]
  );

  const handleTabChange = useCallback(
    (value: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === "mappings") next.delete("tab");
        else next.set("tab", value);
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [effectiveSelectedId]);

  const { data: selectedPrefix, isLoading: loadingPrefix } = useQuery({
    queryKey: ["prefix-admin", effectiveSelectedId],
    queryFn: () =>
      getPrefixById(effectiveSelectedId as string, { includeSequence: true }),
    enabled: !!effectiveSelectedId,
    staleTime: 30_000,
  });

  const deactivateMutation = useMutation({
    mutationFn: () => deactivatePrefix(effectiveSelectedId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prefixes"] });
      queryClient.invalidateQueries({
        queryKey: ["prefix-admin", effectiveSelectedId],
      });
      closeDeactivateModal();
    },
  });

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: 1400, margin: "0 auto" }}>
        <Stack gap="lg">
          <div>
            <Title order={2}>Prefix Configuration</Title>
            <Text size="sm" c="dimmed" mt="xs">
              Manage case prefixes, practice mappings, and insurance eligibility
              rules.
            </Text>
          </div>

          <Group align="stretch" gap="lg" wrap="nowrap">
            {/* Left panel — prefix list */}
            <Paper
              p={0}
              withBorder
              style={{
                width: 300,
                flexShrink: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <PrefixList
                onSelectPrefix={handleSelectPrefix}
                selectedPrefixId={effectiveSelectedId}
              />
            </Paper>

            {/* Right panel — detail (2/3) */}
            <Box style={{ flex: 1, minWidth: 0 }}>
              {!effectiveSelectedId ? (
                <Paper p="xl" withBorder>
                  <Text c="dimmed" ta="center">
                    Select a prefix from the list on the left to view its
                    configuration.
                  </Text>
                </Paper>
              ) : loadingPrefix ? (
                <Paper p="xl" withBorder>
                  <Center>
                    <Loader />
                  </Center>
                </Paper>
              ) : selectedPrefix ? (
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    {/* Header */}
                    <Group justify="space-between" align="flex-start">
                      <div>
                        <Group gap="sm">
                          {selectedPrefix.color ? (
                            <Box
                              style={{
                                backgroundColor: selectedPrefix.color,
                                color: getContrastColor(selectedPrefix.color),
                                borderRadius: 4,
                                padding: "3px 10px",
                                fontWeight: 700,
                                fontSize: 14,
                                fontFamily: "monospace",
                              }}
                            >
                              {selectedPrefix.code}
                            </Box>
                          ) : (
                            <Badge color="blue" variant="filled" size="lg">
                              {selectedPrefix.code}
                            </Badge>
                          )}
                          <Title order={3}>{selectedPrefix.code}</Title>
                          {!selectedPrefix.isActive && (
                            <Badge color="red" variant="light">
                              Inactive
                            </Badge>
                          )}
                          {selectedPrefix.prefixType && (
                            <Badge color="violet" variant="light" size="sm">
                              {selectedPrefix.prefixType.assignmentStrategy.replace(
                                /_/g,
                                " "
                              )}
                            </Badge>
                          )}
                        </Group>
                      </div>
                      <Group gap="xs">
                        <Button
                          variant="light"
                          size="xs"
                          leftSection={<IconEdit size={14} />}
                          onClick={openEditModal}
                        >
                          Edit
                        </Button>
                        {selectedPrefix.isActive && (
                          <Button
                            variant="light"
                            color="red"
                            size="xs"
                            leftSection={<IconX size={14} />}
                            onClick={openDeactivateModal}
                          >
                            Deactivate
                          </Button>
                        )}
                      </Group>
                    </Group>

                    {/* Metadata strip */}
                    <Divider />
                    <Group gap="xl" wrap="wrap">
                      <Box>
                        <Text size="xs" c="dimmed" fw={500} mb={4}>
                          Prefix Type
                        </Text>
                        {selectedPrefix.prefixType ? (
                          <Badge color="blue" variant="light" size="sm">
                            {selectedPrefix.prefixType.name}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Box>
                      <Box>
                        <Text size="xs" c="dimmed" fw={500} mb={4}>
                          Next Case Number
                        </Text>
                        <Text size="sm" fw={600} ff="monospace">
                          {selectedPrefix.sequence?.nextCaseNumber ??
                            "Unavailable"}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" c="dimmed" fw={500} mb={4}>
                          Color
                        </Text>
                        {selectedPrefix.color ? (
                          <Group gap="xs" align="center">
                            <Box
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: 3,
                                backgroundColor: selectedPrefix.color,
                                border: "1px solid var(--mantine-color-gray-3)",
                                flexShrink: 0,
                              }}
                            />
                            <Text size="sm">{selectedPrefix.color}</Text>
                          </Group>
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Box>
                      <Box>
                        <Text size="xs" c="dimmed" fw={500} mb={4}>
                          Fallback Prefix
                        </Text>
                        {selectedPrefix.fallbackPrefix ? (
                          <Badge color="gray" variant="light" size="sm">
                            {selectedPrefix.fallbackPrefix.code}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">
                            None
                          </Text>
                        )}
                      </Box>
                    </Group>
                    <Divider />

                    {/* Tabs */}
                    <Tabs value={activeTab} onChange={handleTabChange}>
                      <Tabs.List>
                        <Tabs.Tab value="mappings">Mappings</Tabs.Tab>
                        <Tabs.Tab value="case-types">
                          Billing Scenarios
                        </Tabs.Tab>
                        <Tabs.Tab value="eligibility">
                          Insurance Eligibility
                        </Tabs.Tab>
                        <Tabs.Tab value="report-identity">
                          Report Identity
                        </Tabs.Tab>
                      </Tabs.List>

                      <Tabs.Panel value="mappings" pt="md">
                        <PrefixMappingsPanel prefixId={effectiveSelectedId} />
                      </Tabs.Panel>

                      <Tabs.Panel value="case-types" pt="md">
                        <PrefixAllowedBillingScenariosPanel
                          allowedBillingScenarios={
                            selectedPrefix.allowedBillingScenarios
                          }
                          onEdit={openEditModal}
                        />
                      </Tabs.Panel>

                      <Tabs.Panel value="eligibility" pt="md">
                        <PrefixInsuranceEligibilityPanel
                          prefixId={effectiveSelectedId}
                        />
                      </Tabs.Panel>

                      <Tabs.Panel value="report-identity" pt="md">
                        <PrefixReportIdentityPanel
                          key={selectedPrefix.id}
                          prefixId={effectiveSelectedId}
                          initialSource={
                            selectedPrefix.reportIdentitySource ||
                            (["S", "G"].includes(selectedPrefix.code)
                              ? "gcp"
                              : "sending_practice")
                          }
                        />
                      </Tabs.Panel>
                    </Tabs>
                  </Stack>
                </Paper>
              ) : null}
            </Box>
          </Group>
        </Stack>
      </Box>

      {/* Edit modal */}
      <PrefixFormModal
        opened={editModalOpened}
        onClose={closeEditModal}
        prefixId={effectiveSelectedId ?? undefined}
        onSuccess={() => {
          closeEditModal();
        }}
      />

      {/* Deactivate confirmation */}
      <Modal
        opened={deactivateModalOpened}
        onClose={closeDeactivateModal}
        title="Deactivate Prefix"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to deactivate prefix{" "}
            <strong>{selectedPrefix?.code}</strong>?
          </Text>
          <Text size="sm" c="dimmed">
            New cases will no longer be matched to this prefix. Existing cases
            are not affected.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDeactivateModal}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => deactivateMutation.mutate()}
              loading={deactivateMutation.isPending}
            >
              Deactivate
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
